import { useCallback, useEffect, useRef, useState } from 'react';
import type { EleicaoSnapshot } from '@/shared/types';
import { eleicaoRepository } from '../../data/eleicaoRepository';
import { ordenarSnapshots } from '../../domain/usecases/cargos';

/** Pede as fotos ao navegador antes de o cartão montar, para já estarem no cache quando o painel aparecer. */
function precarregarFotos(urls: string[], jaPedidas: Set<string>) {
  for (const url of urls) {
    if (!url || jaPedidas.has(url)) continue;
    jaPedidas.add(url);
    const img = new Image();
    img.src = url;
  }
}

export interface UseResultadoEleicaoParams {
  habilitado: boolean;
  /** Intervalo entre consultas ao nosso cache (padrão: 30 s). */
  intervaloMs?: number;
  /** Tempo de cada cargo no ar quando há mais de um (padrão: 20 s). */
  rotacaoMs?: number;
}

/**
 * Resultado da eleição oficial (TSE) em carrossel: Presidente → Governador → Senador da UF.
 *
 * - Dispara a primeira consulta ao ser habilitado e repete a cada `intervaloMs`.
 * - Devolve o cargo da vez; troca a cada `rotacaoMs` (sem timer com um cargo só).
 * - Protegido contra respostas fora de ordem (race conditions) via contador de geração.
 * - Invalida respostas pendentes ao desabilitar ou desmontar, evitando reativações indevidas.
 * - Pré-carrega as fotos de todos os candidatos assim que a lista chega.
 * - Preserva a última lista válida em caso de erro de rede ou oscilação do TSE.
 * - Limpa os timers ao desmontar ou desabilitar.
 */
export function useResultadoEleicao({
  habilitado,
  intervaloMs = 30_000,
  rotacaoMs = 20_000,
}: UseResultadoEleicaoParams): EleicaoSnapshot | null {
  const [lista, setLista] = useState<EleicaoSnapshot[]>([]);
  const [indice, setIndice] = useState(0);
  const habilitadoRef = useRef(habilitado);
  const geracaoRef = useRef(0);
  const fotosPedidasRef = useRef(new Set<string>());

  useEffect(() => {
    habilitadoRef.current = habilitado;
  }, [habilitado]);

  const invalidarRequisicoes = useCallback(() => {
    geracaoRef.current++;
  }, []);

  const carregar = useCallback(async () => {
    if (!habilitadoRef.current) return;
    const geracaoAtual = ++geracaoRef.current;

    try {
      const dados = await eleicaoRepository.snapshots();
      // Descarta se foi desabilitado durante o voo ou se uma geração mais nova já foi disparada
      if (!habilitadoRef.current || geracaoAtual !== geracaoRef.current) return;

      if (dados.length > 0) {
        for (const s of dados) {
          precarregarFotos(s.candidatos.map(c => c.foto_url), fotosPedidasRef.current);
        }
        setLista(ordenarSnapshots(dados));
      }
    } catch {
      // Preserva a última lista em caso de erro, garantindo que o Player
      // não fique sem dados na tela se o TSE oscilar.
    }
  }, []);

  useEffect(() => {
    if (!habilitado) {
      invalidarRequisicoes();
      setLista([]);
      setIndice(0);
      return;
    }

    void carregar();
    const timer = setInterval(() => {
      void carregar();
    }, intervaloMs);

    return () => {
      invalidarRequisicoes();
      clearInterval(timer);
    };
  }, [habilitado, intervaloMs, carregar, invalidarRequisicoes]);

  const total = lista.length;
  useEffect(() => {
    if (!habilitado || total < 2) return;
    const timer = setInterval(() => {
      setIndice(atual => (atual + 1) % total);
    }, rotacaoMs);
    return () => clearInterval(timer);
  }, [habilitado, total, rotacaoMs]);

  return total > 0 ? lista[indice % total] : null;
}
